<script setup lang="ts">
import Datepicker, {
  type PublicMethods,
  type VueDatePickerProps,
} from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

interface CalendarProps {
  position?: VueDatePickerProps['position']
  is24?: VueDatePickerProps['is24']
  enableTimePicker?: VueDatePickerProps['enableTimePicker']
  partialRange?: VueDatePickerProps['partialRange']
  label?: string
  placeholder?: VueDatePickerProps['placeholder']
  textInput?: VueDatePickerProps['textInput']
  name: string
  variant?: 'date' | 'time'
  range?: VueDatePickerProps['range']
  monthChangeOnScroll?: VueDatePickerProps['monthChangeOnScroll']
  minDate?: VueDatePickerProps['minDate']
  maxDate?: VueDatePickerProps['maxDate']
  disabled?: VueDatePickerProps['disabled']
  modelValue?: VueDatePickerProps['modelValue']
}

const emits = defineEmits<{
  (e: 'update:modelValue', value: VueDatePickerProps['modelValue']): void
}>()

const props = withDefaults(defineProps<CalendarProps>(), {
  position: 'left',
  is24: false,
  enableTimePicker: false,
  monthChangeOnScroll: false,
  textInput: false,
  variant: 'date',
  range: false,
  partialRange: false,
  placeholder: 'Select',
  label: 'Date Requested',
})

const dp = ref<PublicMethods>()
const id = useId()

const select = () => {
  if (typeof dp.value === 'undefined') return
  dp.value.selectDate()
}

const close = () => {
  if (typeof dp.value === 'undefined') return
  dp.value.closeMenu()
}

const updateValue = (value: VueDatePickerProps['modelValue']) => {
  emits('update:modelValue', value)
}

const format = (date: Date) => {
  if (!date) return ''
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const name = date.toLocaleString('en-us', { month: 'short' })
  return `${name}, ${month}/${day}/${year}`
}
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<template>
  <div class="w-full">
    <Datepicker
      ref="dp"
      v-bind="{
        ...props,
        ...$attrs,
      }"
      hide-input-icon
      :time-picker="props.variant === 'time'"
      :enable-time-picker="props.variant === 'time'"
      :range="props.variant === 'date' && props.range"
      :auto-position="true"
      :format="format"
      @update:model-value="updateValue"
    >
      <template #dp-input="{ value, onInput }">
        <AppInput
          :id="id"
          type="text"
          class="px-2"
          :name="props.name"
          :model-value="value"
          :min-date="props.minDate"
          :max-date="props.maxDate"
          :disabled="props.disabled"
          :placeholder="props.placeholder"
          absolute-error-placement
          inputmode="none"
          aria-autocomplete="none"
          variant="neutral"
          :label="props.label"
          @input="onInput"
        >
          <template #append>
            <data
              class="flex items-center rounded-md border-y border-r border-slate-200 p-3"
            >
              <AppiconCalendar class="h-5 w-5 text-gray-600" />
            </data>
          </template>
        </AppInput>
      </template>

      <template #input-icon>
        <HeroiconsCalendar class="mx-2 h-6 w-4 text-gray-600" />
      </template>

      <template #action-buttons>
        <AppButton type="button" size="sm" variant="secondary" @click="close">
          Close
        </AppButton>

        <AppButton type="button" size="sm" variant="primary" @click="select">
          Select
        </AppButton>
      </template>

      <template #clear-icon></template>

      <template #arrow-up>
        <HeroiconsChevronUp class="h-4 w-4 text-gray-600" />
      </template>

      <template #arrow-right>
        <HeroiconsChevronRight class="h-4 w-4 text-gray-600" />
      </template>

      <template #arrow-down>
        <HeroiconsChevronDown class="h-4 w-4 text-gray-600" />
      </template>

      <template #arrow-left>
        <HeroiconsChevronLeft class="h-4 w-4 text-gray-600" />
      </template>
    </Datepicker>
  </div>
</template>

<style>
/* Root */
.dp__menu {
  @apply p-3;
}

.dp__menu .dp__arrow_top,
.dp__menu .dp__arrow_bottom {
  @apply hidden;
}

.dp__clear_icon {
  @apply inline-flex;
}

/* Header */
.dp__month_year_col_nav .dp__inner_nav {
  @apply h-auto w-auto p-1.5;
}

/* Main */
.dp__theme_light .dp__active_date {
  @apply bg-brand-danger;
}

.dp__theme_light .dp__today {
  @apply border-primary-600;
}

.dp__theme_light .dp__overlay_action {
  @apply mx-1 h-auto w-full rounded;
}

.dp__theme_light .dp__overlay_cell_active {
  @apply bg-brand-danger hover:bg-primary-700;
}

.dp__theme_light .dp__range_start,
.dp__theme_light .dp__range_end {
  @apply bg-brand-danger;
}

.dp__theme_light .dp__pm_am_button {
  @apply font-medium text-gray-500;
}

/* Footer */
.dp__action_row {
  @apply mt-2 p-0;
}

.dp__selection_preview {
  @apply hidden;
}

.dp__action_buttons {
  @apply flex min-w-full justify-between;
}

.dp__action_buttons .dp__action {
  @apply rounded px-3 py-2 text-sm leading-4;
}

.dp__action_buttons .dp__select {
  @apply bg-brand-danger text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-600;
}

.dp__action_buttons .dp__cancel {
  @apply bg-gray-600 text-white hover:bg-gray-700 focus:ring-2 focus:ring-gray-600;
}
</style>
